import React, { useEffect, useState } from 'react';
import { RotateCcw, Plus, Check } from 'lucide-react';
import { apiRequest } from '../../../lib/api';
import { useCoreContext } from '../../../lib/coreContext';
import { formatCurrency } from '../../../lib/formatters';

export interface FrequentItemDto {
  productId: string;
  name: string;
  price: number;
  priceCents: number;
  imageUrl?: string;
  category?: string;
  inStock: boolean;
  reorderCount: number;
  totalPurchasedQty: number;
  lastOrderedAt: string;
  restockUrgency?: 'HIGH' | 'MEDIUM' | 'LOW';
  recommendedQuantity?: number;
}

interface SmartPantryApiResult {
  ok?: boolean;
  success?: boolean;
  data?: {
    items: Array<{
      productId: string;
      name: string;
      priceCents: number;
      imageUrl?: string;
      recommendedQuantity: number;
      purchaseCount: number;
      restockUrgency: 'HIGH' | 'MEDIUM' | 'LOW';
    }>;
  };
}

interface BuyAgainCarouselProps {
  className?: string;
}

export const BuyAgainCarousel: React.FC<BuyAgainCarouselProps> = ({ className = '' }) => {
  const [items, setItems] = useState<FrequentItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSmartPantry, setIsSmartPantry] = useState(false);
  const [addedIds, setAddedIds] = useState<Record<string, boolean>>({});
  const [allAdded, setAllAdded] = useState(false);
  const core = useCoreContext();

  const isAuthenticated = Boolean(core.currentUser?.id || core.currentUser?._id);

  useEffect(() => {
    if (!isAuthenticated) return;
    let isMounted = true;
    setLoading(true);

    // Try Smart Pantry predictive restock first
    apiRequest<SmartPantryApiResult>('/orders/pantry/recommendations')
      .then((res) => {
        const pantryItems = res?.data?.items || [];
        if (isMounted && Array.isArray(pantryItems) && pantryItems.length > 0) {
          const mapped: FrequentItemDto[] = pantryItems.map((pi) => ({
            productId: pi.productId,
            name: pi.name,
            price: (pi.priceCents || 0) / 100,
            priceCents: pi.priceCents || 0,
            imageUrl: pi.imageUrl,
            inStock: true,
            reorderCount: pi.purchaseCount || 1,
            totalPurchasedQty: pi.purchaseCount || 1,
            lastOrderedAt: new Date().toISOString(),
            restockUrgency: pi.restockUrgency,
            recommendedQuantity: pi.recommendedQuantity || 1
          }));
          setItems(mapped);
          setIsSmartPantry(true);
          return;
        }

        // Fallback to frequent items
        return apiRequest<{ ok: boolean; items: FrequentItemDto[] }>('/orders/frequent-items?limit=10')
          .then((freqRes) => {
            const list = Array.isArray(freqRes) ? freqRes : (freqRes && freqRes.items ? freqRes.items : []);
            if (isMounted && Array.isArray(list)) {
              setItems(list);
              setIsSmartPantry(false);
            }
          });
      })
      .catch(() => {
        // Silent fallback when no previous order history exists
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated || (!loading && items.length === 0)) {
    return null;
  }

  const handleQuickAdd = (item: FrequentItemDto) => {
    if (!item.inStock) return;
    core.setCart((prev) => {
      const idx = prev.findIndex((i) => (i.productId || i.id) === item.productId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: (next[idx].quantity || 1) + 1 };
        return next;
      }
      return [...prev, { id: item.productId, productId: item.productId, quantity: 1, temperaturePreference: 'ambient' }];
    });

    setAddedIds((prev) => ({ ...prev, [item.productId]: true }));
    setTimeout(() => {
      setAddedIds((prev) => ({ ...prev, [item.productId]: false }));
    }, 1500);
  };

  const handleRestockAll = () => {
    core.setCart((prev) => {
      let next = [...prev];
      for (const item of items) {
        if (!item.inStock) continue;
        const idx = next.findIndex((i) => (i.productId || i.id) === item.productId);
        const qty = item.recommendedQuantity || 1;
        if (idx >= 0) {
          next[idx] = { ...next[idx], quantity: (next[idx].quantity || 1) + qty };
        } else {
          next.push({
            id: item.productId,
            productId: item.productId,
            quantity: qty,
            temperaturePreference: 'ambient'
          });
        }
      }
      return next;
    });
    setAllAdded(true);
    setTimeout(() => setAllAdded(false), 2000);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <RotateCcw className="w-4 h-4 text-emerald-400" />
          <h3 className="text-xs font-black uppercase tracking-wider text-slate-200">
            {isSmartPantry ? 'Smart Pantry Restock' : 'Buy Again & Essentials'}
          </h3>
          <span className="text-[10px] font-bold text-slate-400 bg-white/5 px-2 py-0.5 rounded-full">
            {isSmartPantry ? 'Predicted Cadence' : 'Your Regulars'}
          </span>
        </div>
        {isSmartPantry && items.length > 0 && (
          <button
            type="button"
            onClick={handleRestockAll}
            className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 px-2.5 py-1 rounded-full transition flex items-center gap-1"
          >
            {allAdded ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
            {allAdded ? 'Restocked!' : '1-Tap Restock All'}
          </button>
        )}
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-none snap-x">
        {items.map((item) => {
          const isAdded = Boolean(addedIds[item.productId]);
          return (
            <div
              key={item.productId}
              className="flex-shrink-0 w-36 rounded-xl border border-white/10 bg-slate-900/60 p-3 flex flex-col justify-between snap-start transition hover:border-emerald-500/40"
            >
              <div className="space-y-1.5">
                <div className="relative aspect-square w-full rounded-lg bg-black/40 overflow-hidden flex items-center justify-center">
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-full h-full object-contain p-1"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-2xl">🥑</span>
                  )}
                  {item.restockUrgency === 'HIGH' ? (
                    <span className="absolute top-1 left-1 text-[8px] font-black bg-rose-500/90 text-white px-1.5 py-0.5 rounded shadow">
                      Running Low
                    </span>
                  ) : item.restockUrgency === 'MEDIUM' ? (
                    <span className="absolute top-1 left-1 text-[8px] font-black bg-cyan-500/90 text-black px-1.5 py-0.5 rounded shadow">
                      Restock Soon
                    </span>
                  ) : null}
                  {item.reorderCount > 1 && (
                    <span className="absolute bottom-1 right-1 text-[9px] font-black bg-emerald-500/90 text-black px-1.5 py-0.2 rounded font-mono">
                      {item.reorderCount}x
                    </span>
                  )}
                </div>
                <p className="text-xs font-bold text-slate-100 line-clamp-2 leading-tight">
                  {item.name}
                </p>
              </div>

              <div className="mt-2.5 flex items-center justify-between pt-1 border-t border-white/5">
                <span className="text-xs font-black text-emerald-400">
                  {formatCurrency(item.price)}
                </span>
                <button
                  type="button"
                  onClick={() => handleQuickAdd(item)}
                  disabled={!item.inStock || isAdded}
                  className={`p-1.5 rounded-lg font-bold text-xs flex items-center justify-center transition ${
                    isAdded
                      ? 'bg-emerald-500 text-black'
                      : 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500 hover:text-black'
                  }`}
                  aria-label={`Add ${item.name} to cart`}
                >
                  {isAdded ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
export default BuyAgainCarousel;
